import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@akuna/ui";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useAddEvolucao } from "@/hooks/useHospitalization";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const toOptionalFloat = (max: number) =>
  z.string().optional().refine(
    (v) => !v || (!isNaN(parseFloat(v)) && parseFloat(v) >= 0 && parseFloat(v) <= max),
    { message: "Valor inválido" }
  );

const schema = z.object({
  peso_kg:       toOptionalFloat(999),
  temperatura_c: toOptionalFloat(99.9),
  fc_bpm:        toOptionalFloat(999),
  fr_rpm:        toOptionalFloat(999),
  tpc_s:         toOptionalFloat(999),
  condicao_geral: z.string().optional(),
  observacoes:   z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface Evolucao {
  id: string;
  registrado_at: string;
  peso_kg?: number | null;
  temperatura_c?: number | null;
  fc_bpm?: number | null;
  fr_rpm?: number | null;
  tpc_s?: number | null;
  condicao_geral?: number | null;
  observacoes?: string | null;
}

interface EvolucaoTabProps {
  hospitalizationId: string;
  customerId: string;
  evolucoes: Evolucao[];
}

const VITAIS = [
  { name: "peso_kg" as const, labelKey: "evolucao.peso" },
  { name: "temperatura_c" as const, labelKey: "evolucao.temperatura" },
  { name: "fc_bpm" as const, labelKey: "evolucao.fc" },
  { name: "fr_rpm" as const, labelKey: "evolucao.fr" },
  { name: "tpc_s" as const, labelKey: "evolucao.tpc" },
];

export function EvolucaoTab({ hospitalizationId, customerId, evolucoes }: EvolucaoTabProps) {
  const { t, i18n } = useTranslation("hospitalization");
  const [showForm, setShowForm] = useState(false);
  const addMutation = useAddEvolucao();
  const locale = i18n.language === "pt" ? ptBR : undefined;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {},
  });

  async function onSubmit(values: FormValues) {
    try {
      await addMutation.mutateAsync({
        hospitalization_id: hospitalizationId,
        customer_id: customerId,
        peso_kg: values.peso_kg ? parseFloat(values.peso_kg) : null,
        temperatura_c: values.temperatura_c ? parseFloat(values.temperatura_c) : null,
        fc_bpm: values.fc_bpm ? parseInt(values.fc_bpm) : null,
        fr_rpm: values.fr_rpm ? parseInt(values.fr_rpm) : null,
        tpc_s: values.tpc_s ? parseFloat(values.tpc_s) : null,
        condicao_geral: values.condicao_geral ? parseInt(values.condicao_geral) : null,
        observacoes: values.observacoes || null,
      });
      toast.success(t("success.evolucaoSaved"));
      form.reset();
      setShowForm(false);
    } catch {
      toast.error(t("errors.saveFailed"));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowForm((v) => !v)}>
          <Plus className="h-4 w-4 mr-1" />
          {t("evolucao.novoRegistro")}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="pt-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {VITAIS.map(({ name, labelKey }) => (
                    <FormField
                      key={name}
                      control={form.control}
                      name={name}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t(labelKey)}</FormLabel>
                          <FormControl>
                            <Input type="number" step="any" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                  <FormField
                    control={form.control}
                    name="condicao_geral"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("evolucao.condicaoGeral")}</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {[1, 2, 3, 4, 5].map((n) => (
                              <SelectItem key={n} value={String(n)}>
                                {t(`evolucao.condicaoOpcoes.${n}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="observacoes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("evolucao.observacoes")}</FormLabel>
                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                    {t("evolucao.cancelar")}
                  </Button>
                  <Button type="submit" disabled={addMutation.isPending}>
                    {t("evolucao.salvar")}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}

      {evolucoes.length === 0 && !showForm && (
        <p className="text-sm text-muted-foreground text-center py-8">
          {t("evolucao.semRegistros")}
        </p>
      )}

      {[...evolucoes]
        .sort(
          (a, b) =>
            new Date(b.registrado_at).getTime() - new Date(a.registrado_at).getTime()
        )
        .map((ev) => (
          <Card key={ev.id}>
            <CardContent className="pt-3 pb-3">
              <p className="text-xs text-muted-foreground mb-2">
                {format(new Date(ev.registrado_at), "dd/MM/yyyy HH:mm", { locale })}
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-sm mb-2">
                {ev.peso_kg != null && (
                  <span>
                    <strong>{t("evolucao.peso")}:</strong> {ev.peso_kg}kg
                  </span>
                )}
                {ev.temperatura_c != null && (
                  <span>
                    <strong>{t("evolucao.temperatura")}:</strong> {ev.temperatura_c}°C
                  </span>
                )}
                {ev.fc_bpm != null && (
                  <span>
                    <strong>{t("evolucao.fc")}:</strong> {ev.fc_bpm}
                  </span>
                )}
                {ev.fr_rpm != null && (
                  <span>
                    <strong>{t("evolucao.fr")}:</strong> {ev.fr_rpm}
                  </span>
                )}
                {ev.tpc_s != null && (
                  <span>
                    <strong>{t("evolucao.tpc")}:</strong> {ev.tpc_s}s
                  </span>
                )}
              </div>
              {ev.condicao_geral != null && (
                <p className="text-sm">
                  {t("evolucao.condicaoGeral")}:{" "}
                  {t(`evolucao.condicaoOpcoes.${ev.condicao_geral}`)}
                </p>
              )}
              {ev.observacoes && (
                <p className="text-sm mt-1 text-muted-foreground">{ev.observacoes}</p>
              )}
            </CardContent>
          </Card>
        ))}
    </div>
  );
}
